// PageHeader — the top of every admin page, once.
//
// Replaces 24 hand-rolled headers that each picked their own heading size,
// spacing and action placement. Breadcrumbs are passed explicitly rather than
// derived from the route tree: several admin routes are flat files whose path
// (`_authed.pages.$id_.blocks.$blockId`) does not match the human hierarchy.

import { cn } from '@fajrarabi/ui';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

import { Icon } from './Icon';

export interface Crumb {
  label: string;
  /** Omit on the current page — the last crumb is never a link. */
  to?: string;
  params?: Record<string, string>;
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  crumbs?: readonly Crumb[];
  /** Primary + secondary actions, rendered at the inline end. */
  actions?: ReactNode;
  /** Status badge or similar, rendered beside the title. */
  badge?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  crumbs,
  actions,
  badge,
  className,
}: PageHeaderProps) {
  return (
    <div className={cn('mb-6', className)}>
      {crumbs && crumbs.length > 0 && <Breadcrumbs crumbs={crumbs} />}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-xl font-bold tracking-tight text-admin-900 md:text-2xl">{title}</h1>
            {badge}
          </div>
          {description && (
            <p className="mt-1.5 max-w-2xl text-sm leading-relaxed text-admin-500">{description}</p>
          )}
        </div>

        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  );
}

function Breadcrumbs({ crumbs }: { crumbs: readonly Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="mb-2">
      <ol className="flex flex-wrap items-center gap-1 text-xs text-admin-500">
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <li key={`${crumb.label}-${i}`} className="flex items-center gap-1">
              {crumb.to && !isLast ? (
                <Link
                  to={crumb.to}
                  params={crumb.params}
                  className="rounded transition-colors hover:text-admin-800 hover:underline"
                >
                  {crumb.label}
                </Link>
              ) : (
                <span
                  className={cn(isLast && 'font-medium text-admin-700')}
                  aria-current={isLast ? 'page' : undefined}
                >
                  {crumb.label}
                </span>
              )}
              {!isLast && (
                // The separator points along the reading direction, so it must
                // mirror in RTL even though the admin chrome is English.
                <Icon name="chevronEnd" className="h-3 w-3 text-admin-300 rtl:-scale-x-100" />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
